import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

/**
 * Meta Conversions API server route.
 *
 * Mirrors the browser Pixel events server-side using the same `event_id`
 * for deduplication, with the first-touch gclid/UTM parameters that survive
 * redirects (captured client-side and read from the request body). No health,
 * diagnosis, or form-answer data is ever sent — only the event name, a
 * non-identifying content category, and ad attribution parameters.
 */

const Body = z.object({
  event_name: z.string().min(1).max(64),
  event_id: z.string().max(128).optional(),
  content_name: z.string().max(255).optional(),
  content_category: z.string().max(64).optional(),
  value: z.number().nonnegative().optional(),
  currency: z.string().max(8).optional(),
  event_source_url: z.string().url().optional(),
  ad_params: z
    .object({
      gclid: z.string().max(255).optional(),
      utm_source: z.string().max(255).optional(),
      utm_medium: z.string().max(255).optional(),
      utm_campaign: z.string().max(255).optional(),
      utm_term: z.string().max(255).optional(),
      utm_content: z.string().max(255).optional(),
      campaign_id: z.string().max(255).optional(),
      ad_group_id: z.string().max(255).optional(),
      ad_id: z.string().max(255).optional(),
      ad_account_id: z.string().max(255).optional(),
    })
    .optional(),
});

export const Route = createFileRoute("/api/meta-capi")({
  server: {
    handlers: {
      OPTIONS: async () => {
        return new Response(null, {
          status: 204,
          headers: {
            "Access-Control-Allow-Origin": "same-origin",
            "Access-Control-Allow-Methods": "POST, OPTIONS",
            "Access-Control-Allow-Headers": "Content-Type",
          },
        });
      },
      POST: async ({ request }) => {
        let raw: unknown;
        try {
          raw = await request.json();
        } catch {
          return new Response(
            JSON.stringify({ ok: false, error: "bad_json" }),
            {
              status: 400,
              headers: { "Content-Type": "application/json" },
            },
          );
        }

        const parsed = Body.safeParse(raw);
        if (!parsed.success) {
          return new Response(
            JSON.stringify({ ok: false, error: "invalid_payload" }),
            { status: 400, headers: { "Content-Type": "application/json" } },
          );
        }
        const data = parsed.data;

        const pixelId = process.env.META_PIXEL_ID;
        const token = process.env.META_CAPI_TOKEN;
        // If the server-side token isn't provisioned, ack the request so the
        // browser Pixel still fires and callers don't error. The route stays
        // reachable but becomes a no-op until the token is set.
        if (!pixelId || !token) {
          return new Response(
            JSON.stringify({ ok: true, sent: false, reason: "not_configured" }),
            { status: 202, headers: { "Content-Type": "application/json" } },
          );
        }

        const nowMs = Date.now();
        const eventId =
          data.event_id ??
          `${data.event_name}:${nowMs}:${Math.random().toString(36).slice(2)}`;

        const userData: Record<string, string> = {};
        const ad = data.ad_params ?? {};
        if (ad.utm_campaign) userData.campaign_name = ad.utm_campaign;
        // gclid is mapped to the fb_click_id parameter in Graph API v18+.
        if (ad.gclid) userData.fb_click_id = ad.gclid;

        const payload = {
          data: [
            {
              event_name: data.event_name,
              event_id: eventId,
              event_time: Math.floor(nowMs / 1000),
              action_source: "website",
              event_source_url: data.event_source_url,
              custom_data: {
                content_name: data.content_name,
                content_category: data.content_category,
                value: data.value,
                currency: data.currency ?? "USD",
              },
              user_data: userData,
              // mark as browser-pixel-deduplicated when the caller passed an id
              ...{},
            },
          ],
        };

        const url = `https://graph.facebook.com/v18.0/${pixelId}/events?access_token=${token}`;

        try {
          const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          if (!res.ok) {
            return new Response(
              JSON.stringify({ ok: false, sent: false, status: res.status }),
              { status: 502, headers: { "Content-Type": "application/json" } },
            );
          }
          return new Response(
            JSON.stringify({ ok: true, sent: true, event_id: eventId }),
            { status: 200, headers: { "Content-Type": "application/json" } },
          );
        } catch {
          return new Response(
            JSON.stringify({ ok: false, sent: false, error: "fetch_failed" }),
            { status: 502, headers: { "Content-Type": "application/json" } },
          );
        }
      },
    },
  },
  component: () => null,
});
